import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CornerDownRight, Plus, Save, Trash2 } from 'lucide-react';
import {
  MENU_LOCATIONS,
  type MenuItem,
  type MenuLocation,
  type TranslatableString,
} from '@geoic/shared';
import { api } from '@/lib/api-client.js';
import { queryKeys } from '@/lib/query-keys.js';
import { errorMessage } from '@/lib/api-error.js';
import { cn } from '@/lib/cn.js';
import { Button } from '@/components/ui/Button.js';
import { Card, CardBody, CardHeader } from '@/components/ui/Card.js';
import { Checkbox, Input, Select } from '@/components/ui/Field.js';
import { ConfirmDialog } from '@/components/ui/Modal.js';
import { useToast } from '@/components/ui/Toast.js';
import { SortableList } from '@/components/ui/SortableList.js';
import { TranslatableInput } from '@/components/form/TranslatableInput.js';
import { useBlockOptions } from '@/features/blocks/useBlockOptions.js';
import { newBlockId } from '@/features/blocks/block-catalog.js';
import { useUnsavedGuard } from '../useUnsavedGuard.js';

const LOCATION_LABELS: Record<MenuLocation, string> = {
  header: 'Yuqori menyu',
  footer: 'Pastki menyu',
  'footer-secondary': 'Pastki menyu (ikkinchi ustun)',
};

const LINK_TYPES = [
  { value: 'page', label: 'Sahifa', collection: 'pages' },
  { value: 'news', label: 'Yangilik', collection: 'news' },
  { value: 'service', label: 'Xizmat', collection: 'services' },
  { value: 'product', label: 'Mahsulot', collection: 'products' },
  { value: 'category', label: 'Kategoriya', collection: 'categories' },
  { value: 'project', label: 'Loyiha', collection: 'projects' },
  { value: 'course', label: 'Kurs', collection: 'courses' },
  { value: 'external', label: 'Tashqi havola', collection: null },
  { value: 'none', label: 'Havolasiz (faqat sarlavha)', collection: null },
] as const;

type LinkType = (typeof LINK_TYPES)[number]['value'];

/**
 * Ichki band — unda `children` YO'Q.
 *
 * Sxemada ham shunday (`menuItemBase`): ikki darajali chegara tip
 * darajasida ushlab turiladi, shuning uchun uchinchi darajani qo'shib
 * yuborish umuman kompilyatsiya bo'lmaydi.
 */
type MenuChild = MenuItem['children'][number];
type AnyMenuItem = MenuItem | MenuChild;

const menusApi = {
  get: (location: MenuLocation) =>
    api.get<{ data: { location: MenuLocation; items: MenuItem[] } }>(`/menus/${location}`),
  save: (location: MenuLocation, items: MenuItem[]) =>
    api.put<{ data: { items: MenuItem[] } }>(`/menus/${location}`, { items }),
};

/**
 * Menyu konstruktori (D5 §2).
 *
 * Ikki daraja bilan cheklangan (`EMBEDDED_LIMITS.MENU_DEPTH`): uch darajali
 * menyu telefonda ochib bo'lmaydigan holga keladi, shuning uchun ichki
 * bandga yana ichki band qo'shish umuman taklif qilinmaydi.
 */
export function MenusPage() {
  const [location, setLocation] = useState<MenuLocation>('header');
  const [items, setItems] = useState<MenuItem[]>([]);
  const [dirty, setDirty] = useState(false);
  const [selected, setSelected] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<AnyMenuItem | null>(null);

  const toast = useToast();
  const queryClient = useQueryClient();
  const unsaved = useUnsavedGuard(dirty);

  const menu = useQuery({
    queryKey: queryKeys.content.one('menus', location),
    queryFn: () => menusApi.get(location),
  });

  useEffect(() => {
    if (!menu.data) return;
    setItems(menu.data.data.items ?? []);
    setDirty(false);
    setSelected(null);
  }, [menu.data]);

  const save = useMutation({
    mutationFn: () => menusApi.save(location, withOrder(items)),
    onSuccess: async () => {
      setDirty(false);
      toast.success('Menyu saqlandi');
      await queryClient.invalidateQueries({ queryKey: queryKeys.content.all('menus') });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  function update(next: MenuItem[]): void {
    setItems(next);
    setDirty(true);
  }

  function addItem(parentId: string | null): void {
    const fresh: MenuChild = {
      _id: newBlockId(),
      label: { uz: '', ru: '', en: '' },
      linkType: 'none',
      targetId: null,
      url: null,
      target: '_self',
      icon: null,
      order: 0,
      isVisible: true,
    };

    if (parentId === null) {
      update([...items, { ...fresh, children: [] }]);
    } else {
      update(
        items.map((item) =>
          item._id === parentId ? { ...item, children: [...item.children, fresh] } : item,
        ),
      );
    }
    setSelected(fresh._id);
  }

  function patchItem(id: string, patch: Partial<MenuChild>): void {
    update(
      items.map((item) => {
        if (item._id === id) return { ...item, ...patch };
        return {
          ...item,
          children: item.children.map((child) =>
            child._id === id ? { ...child, ...patch } : child,
          ),
        };
      }),
    );
  }

  function removeItem(id: string): void {
    update(
      items
        .filter((item) => item._id !== id)
        .map((item) => ({
          ...item,
          children: item.children.filter((child) => child._id !== id),
        })),
    );
    setPendingDelete(null);
    if (selected === id) setSelected(null);
  }

  const selectedItem: AnyMenuItem | null =
    items.find((item) => item._id === selected) ??
    items.flatMap((item) => item.children).find((child) => child._id === selected) ??
    null;

  return (
    <div className="space-y-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-n-900">Menyu</h1>
        <Button
          icon={<Save className="size-4" />}
          loading={save.isPending}
          onClick={() => save.mutate()}
        >
          Saqlash
        </Button>
      </div>

      <div className="flex flex-wrap gap-1">
        {MENU_LOCATIONS.map((value) => (
          <button
            key={value}
            type="button"
            aria-pressed={location === value}
            onClick={() => {
              if (dirty && !window.confirm('Saqlanmagan o‘zgarishlar yo‘qoladi. Davom etilsinmi?'))
                return;
              setLocation(value);
            }}
            className={cn(
              ' px-3 py-1.5 text-sm font-medium transition-colors',
              location === value ? 'bg-brand-600 text-white' : 'bg-n-100 text-n-600 hover:bg-n-200',
            )}
          >
            {LOCATION_LABELS[value]}
          </button>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-[1fr_380px]">
        <Card>
          <CardHeader
            title={LOCATION_LABELS[location]}
            description="Bandlarni surib tartiblang. Ichki band qo‘shish uchun bandning o‘ng tomonidagi «+» ni bosing"
          />
          <CardBody className="space-y-3">
            <SortableList
              items={items}
              getId={(item) => item._id}
              onReorder={update}
              loading={menu.isLoading}
              emptyTitle="Menyu bo‘sh"
              rowClassName={(item) =>
                cn(
                  item._id === selected && 'border-brand-500 ring-1 ring-brand-500',
                  !item.isVisible && 'opacity-60',
                )
              }
              renderRow={(item) => (
                <div className="min-w-0 flex-1 space-y-2">
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={() => setSelected(item._id)}
                      className="min-w-0 flex-1 truncate text-left text-sm font-medium text-n-800"
                    >
                      {item.label.uz || '(nomsiz band)'}
                    </button>
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label="Ichki band qo‘shish"
                      disabled={item.children.length >= 40}
                      onClick={() => addItem(item._id)}
                    >
                      <Plus className="size-4" aria-hidden />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      aria-label="O‘chirish"
                      onClick={() => setPendingDelete(item)}
                    >
                      <Trash2 className="size-4 text-error-600" aria-hidden />
                    </Button>
                  </div>

                  {item.children.length > 0 && (
                    <div className="ml-4 border-l border-n-200 pl-3">
                      <SortableList
                        items={item.children}
                        getId={(child) => child._id}
                        onReorder={(children) =>
                          update(
                            items.map((row) => (row._id === item._id ? { ...row, children } : row)),
                          )
                        }
                        rowClassName={(child) =>
                          cn(
                            child._id === selected && 'border-brand-500 ring-1 ring-brand-500',
                            !child.isVisible && 'opacity-60',
                          )
                        }
                        renderRow={(child) => (
                          <div className="flex min-w-0 flex-1 items-center gap-1">
                            <CornerDownRight className="size-3.5 text-n-200" aria-hidden />
                            <button
                              type="button"
                              onClick={() => setSelected(child._id)}
                              className="min-w-0 flex-1 truncate text-left text-sm text-n-800"
                            >
                              {child.label.uz || '(nomsiz band)'}
                            </button>
                            <Button
                              size="sm"
                              variant="ghost"
                              aria-label="O‘chirish"
                              onClick={() => setPendingDelete(child)}
                            >
                              <Trash2 className="size-4 text-error-600" aria-hidden />
                            </Button>
                          </div>
                        )}
                      />
                    </div>
                  )}
                </div>
              )}
            />

            <Button
              variant="outline"
              icon={<Plus className="size-4" />}
              disabled={items.length >= 40}
              onClick={() => addItem(null)}
            >
              Band qo‘shish
            </Button>
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Band" />
          <CardBody>
            {selectedItem ? (
              <MenuItemForm
                item={selectedItem}
                onChange={(patch) => patchItem(selectedItem._id, patch)}
              />
            ) : (
              <p className="py-8 text-center text-sm text-n-600">
                Tahrirlash uchun banddan birini tanlang.
              </p>
            )}
          </CardBody>
        </Card>
      </div>

      <ConfirmDialog
        open={pendingDelete !== null}
        title="Bandni o‘chirish"
        message={deleteMessage(pendingDelete)}
        confirmLabel="O‘chirish"
        danger
        onConfirm={() => pendingDelete && removeItem(pendingDelete._id)}
        onCancel={() => setPendingDelete(null)}
      />

      <ConfirmDialog
        open={unsaved.blocked}
        title="Saqlanmagan o‘zgarishlar"
        message="Menyudagi o‘zgarishlar saqlanmagan. Chiqib ketsangiz ular yo‘qoladi."
        confirmLabel="Baribir chiqish"
        danger
        onConfirm={unsaved.proceed}
        onCancel={unsaved.cancel}
      />
    </div>
  );
}

/** Ichki bandlari bor bandni o'chirish — nima yo'qolishini aniq aytadi. */
function deleteMessage(item: AnyMenuItem | null): string {
  if (!item) return '';
  const label = item.label.uz || '(nomsiz band)';
  const children = 'children' in item ? item.children.length : 0;

  return children > 0
    ? `«${label}» va uning ${children} ta ichki bandi o‘chiriladi.`
    : `«${label}» o‘chiriladi.`;
}

function MenuItemForm({
  item,
  onChange,
}: {
  item: AnyMenuItem;
  onChange: (patch: Partial<MenuChild>) => void;
}) {
  const config = LINK_TYPES.find((type) => type.value === item.linkType);
  const targets = useBlockOptions(config?.collection ?? 'pages', Boolean(config?.collection));

  return (
    <div className="space-y-4">
      <TranslatableInput
        label="Band nomi"
        required
        value={item.label as TranslatableString}
        onChange={(label) => onChange({ label })}
      />

      <Select
        label="Havola turi"
        value={item.linkType}
        options={LINK_TYPES.map((type) => ({ value: type.value, label: type.label }))}
        onChange={(event) =>
          // Tur o'zgarganda eski maqsad TOZALANADI: aks holda sahifa
          // identifikatori mahsulot havolasida qolib, sayt 404 berardi.
          onChange({ linkType: event.target.value as LinkType, targetId: null, url: null })
        }
      />

      {config?.collection && (
        <Select
          label="Maqsad"
          value={item.targetId ?? ''}
          placeholder={targets.isLoading ? 'Yuklanmoqda…' : 'Tanlanmagan'}
          options={targets.options}
          onChange={(event) => onChange({ targetId: event.target.value || null })}
        />
      )}

      {item.linkType === 'external' && (
        <Input
          label="Havola"
          value={item.url ?? ''}
          placeholder="https://…"
          onChange={(event) => onChange({ url: event.target.value || null })}
        />
      )}

      <Select
        label="Qayerda ochilsin"
        value={item.target}
        options={[
          { value: '_self', label: 'Shu oynada' },
          { value: '_blank', label: 'Yangi oynada' },
        ]}
        onChange={(event) => onChange({ target: event.target.value as '_self' | '_blank' })}
      />

      <Input
        label="Belgi (ikon)"
        value={item.icon ?? ''}
        placeholder="phone"
        onChange={(event) => onChange({ icon: event.target.value || null })}
        hint="lucide.dev dagi belgi nomi. Ixtiyoriy"
      />

      <Checkbox
        label="Menyuda ko‘rsatilsin"
        checked={item.isVisible}
        onChange={(event) => onChange({ isVisible: event.target.checked })}
      />
    </div>
  );
}

/** Tartib massivdagi o'rindan olinadi — server `order` bo'yicha saralaydi. */
function withOrder(items: MenuItem[]): MenuItem[] {
  return items.map((item, index) => ({
    ...item,
    order: index,
    children: item.children.map((child, childIndex) => ({ ...child, order: childIndex })),
  }));
}
